// scheduleConflicts.js — pure classifier for individual-technician
// double-booking. NO React, NO Firestore — data in, buckets out — so mobile can
// reuse the exact same rules later with a corpus fetched a different way. It must
// not assume where the jobs came from. See plan
// crew-resources-audit/09-tech-double-booking-web-plan.md.

const isValidDate = (d) => d instanceof Date && !isNaN(d.getTime());

/**
 * Sort every job a technician is already on into two buckets, relative to the
 * job being assigned:
 *   - sameWindow: same arrival window + same day (normal routing — informational)
 *   - clashes:    a genuine exclusive-overlap double-booking (warning)
 * Non-overlapping jobs fall out entirely.
 *
 * All dates are JS Date objects. The caller converts Firestore Timestamps,
 * recomputes the current job's window, and derives each job's windowKey/dayKey
 * before calling — so this helper needs no date library.
 *
 * @param {Object}   args
 * @param {string}   args.technicianId     the tech being added
 * @param {Object}   args.currentJob       { start:Date, end:Date, windowKey:string|null, dayKey:string|null }
 *                                          windowKey & dayKey are non-null ONLY when the job being
 *                                          assigned is a window-mode single-day Job; null otherwise
 *                                          (exact mode, multi-day) — which is what makes an
 *                                          exact/multi-day current job unable to be "same-window".
 * @param {Array}    args.jobs             [{ id, eventType, status, assignedTo:string[],
 *                                            start:Date, end:Date, windowKey:string|null,
 *                                            dayKey:string|null, title, customerName }]
 * @param {string}   [args.excludeJobId]   the job being edited (excludes itself)
 * @returns {{ sameWindow: Array, clashes: Array }}
 */
export function classifyTechScheduleConflicts({ technicianId, currentJob, jobs, excludeJobId }) {
  const sameWindow = [];
  const clashes = [];

  if (!currentJob || !isValidDate(currentJob.start) || !isValidDate(currentJob.end)) {
    return { sameWindow, clashes };
  }

  for (const c of (jobs || [])) {
    if (!c) continue;
    if (excludeJobId && c.id === excludeJobId) continue;              // the job being edited excludes itself
    if (c.eventType !== 'Job') continue;                             // only Jobs make a tech busy
    if (c.status === 'Canceled' || c.status === 'Completed') continue;
    if (!(c.assignedTo || []).includes(technicianId)) continue;      // the tech being added is on it
    if (!isValidDate(c.start) || !isValidDate(c.end)) continue;

    // Same arrival window + same day = normal routing, not a double-book. Both
    // must carry a non-null window key; an exact/multi-day job has windowKey
    // null and can never land here (so it always falls to the overlap test).
    const sameWindowSameDay =
      !!currentJob.windowKey && !!c.windowKey &&
      currentJob.windowKey === c.windowKey &&
      currentJob.dayKey === c.dayKey;

    if (sameWindowSameDay) {
      sameWindow.push(c);
      continue;
    }

    // Exclusive overlap: touching endpoints (end == start) do NOT conflict.
    if (currentJob.start < c.end && c.start < currentJob.end) {
      clashes.push(c);
    }
  }

  return { sameWindow, clashes };
}
